import { isPlatformBrowser, isPlatformServer } from '@angular/common'
import {
  Injector,
  NgModule,
  PLATFORM_ID,
  REQUEST_CONTEXT,
  TransferState,
  inject,
  makeStateKey,
} from '@angular/core'

import type { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { ApolloLink, InMemoryCache } from '@apollo/client'
import { SetContextLink } from '@apollo/client/link/context'
import { provideApollo } from 'apollo-angular'
import { HttpLink } from 'apollo-angular/http'

import { environment } from '../../environments/environment'
import { InternationalizationService } from '../services/internationalization.service'
import type { SsrRequestContext } from '../shared/ssr-request-context'

/**
 * Holds the server's `InMemoryCache` contents so the browser can start from
 * them instead of refetching everything the server already fetched during the
 * render. Without this, every resolver runs twice per page load: once on the
 * server and once more on the navigation that hydrates it.
 */
const APOLLO_CACHE_STATE_KEY =
  makeStateKey<NormalizedCacheObject>('apollo.cache')

@NgModule({
  providers: [provideApollo(createApollo)],
})
export class GraphQLModule {}

export function createApollo(): ApolloClient.Options {
  const httpLink = inject(HttpLink)
  const platformId = inject(PLATFORM_ID)
  const injector = inject(Injector)

  // The Apollo Client (and this factory) is built once and reused across
  // requests, so anything request-scoped - REQUEST_CONTEXT included - has to
  // be read fresh from the injector *inside* the context callback below,
  // which Apollo re-invokes for every operation, not up here.
  const authLink = new SetContextLink(prevContext => {
    const headers = prevContext.headers as Record<string, string> | undefined
    let token = ''
    if (isPlatformBrowser(platformId)) {
      token = localStorage.getItem('token') ?? ''
    }

    let acceptLanguage = 'nl'
    try {
      const i18n = injector.get(InternationalizationService)
      acceptLanguage = i18n.getLanguagePreference()
    } catch {
      // Injector may not be able to resolve
    }

    // REQUEST_CONTEXT is only non-null for a real incoming SSR request
    // (never in the browser, and never during build-time/dev-time route
    // extraction - see server.ts, the only place process.env.SSR_REQUEST_SECRET
    // is read), so the secret itself never ships to the browser.
    const requestContext = injector.get(
      REQUEST_CONTEXT,
      null
    ) as SsrRequestContext | null
    const ssrRequestSecret = requestContext?.ssrRequestSecret
    const ssrApiUrl = requestContext?.ssrApiUrl

    return {
      // apollo-angular's HttpLink prefers `context.uri` over the `uri` it was
      // created with, which is the only place an SSR-only endpoint can be
      // applied: the link below is created once, while this callback runs per
      // operation and can therefore see the request context. Browser
      // operations never take this branch and keep using environment.apiUrl.
      ...(isPlatformServer(platformId) && ssrApiUrl
        ? { uri: `${ssrApiUrl}/api/graphql` }
        : {}),
      headers: {
        ...headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Accept-Language': acceptLanguage,
        ...(isPlatformServer(platformId) && ssrRequestSecret
          ? { 'x-ssr-secret': ssrRequestSecret }
          : {}),
      },
    }
  })

  const http = httpLink.create({ uri: `${environment.apiUrl}/api/graphql` })

  const transferState = inject(TransferState)

  const cache = new InMemoryCache({
    typePolicies: {
      Page: {
        // Payload returns a different title/content per locale under the same
        // id, so normalising by id would let one locale's copy overwrite the
        // other's. Embedded instead, which stores it under the locale-specific
        // `Pages(locale:)` field key - the same reason Magazine_Media below
        // opts out.
        keyFields: false,
      },
      Magazine_Media: {
        keyFields: false,
        merge(existing, incoming) {
          return { ...existing, ...incoming }
        },
      },
    },
  })

  if (isPlatformServer(platformId)) {
    // Serialized into the HTML after the render completes.
    transferState.onSerialize(APOLLO_CACHE_STATE_KEY, () => cache.extract())
  } else {
    const serverState = transferState.get(APOLLO_CACHE_STATE_KEY, null)
    if (serverState) {
      cache.restore(serverState)
      // Only useful once; dropping it keeps it out of later serializations.
      transferState.remove(APOLLO_CACHE_STATE_KEY)
    }
  }

  return {
    link: ApolloLink.from([authLink, http]),
    cache,
  }
}
